// src/pages/admin/AdminAbilitiesScorer.jsx

import React, { useEffect, useState } from 'react';
import { matchingModelAPI } from '../../services/api';
import { Spinner } from '../../components/shared/UI';
import toast from 'react-hot-toast';
import { Save, RotateCcw, Layers, Clock } from 'lucide-react';

const DEFAULTS = {
  grades: {
    grade1: { enabled: true,  weight: 25 },
    grade2: { enabled: true,  weight: 30 },
    grade3: { enabled: true,  weight: 30 },
    grade4: { enabled: false, weight: 15 },
  },
  durationBoost: {
    enabled: true,
    maxBoostPercent: 20,
    halfLifeMonths: 24,
  },
};

const GRADE_INFO = [
  { key: 'grade1', label: 'Grade 1 — Collated (All)',
    desc: 'All roles & tasks across all seeker experience items are collated and compared to the collated roles & tasks of the position.' },
  { key: 'grade2', label: 'Grade 2 — Per Position Name',
    desc: 'Roles & tasks are collated per seeker experience item and compared to the collated roles & tasks of the position.' },
  { key: 'grade3', label: 'Grade 3 — Per Role',
    desc: 'Tasks are collated per seeker experience role and compared to tasks collated per role in the position.' },
  { key: 'grade4', label: 'Grade 4 — Individual Tasks',
    desc: 'Each seeker experience task is individually compared to each position task.' },
];

/* ── Toggle row ──────────────────────────────────────────── */
const GradeRow = ({ info, grade, onChange }) => (
  <div style={{ padding: '14px 0' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ position: 'relative', display: 'inline-block', width: 42, height: 24, flexShrink: 0 }}>
            <input type="checkbox" checked={grade.enabled}
              onChange={e => onChange({ enabled: e.target.checked })}
              style={{ opacity: 0, width: 0, height: 0 }} />
            <span style={{
              position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: 12,
              background: grade.enabled ? '#28A745' : '#ccc', transition: 'background 0.2s',
            }}>
              <span style={{
                position: 'absolute', height: 18, width: 18, left: grade.enabled ? 20 : 4, top: 3,
                background: '#fff', borderRadius: '50%', transition: 'left 0.2s',
              }} />
            </span>
          </label>
          <label className="form-label" style={{ marginBottom: 0 }}>{info.label}</label>
        </div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 4, paddingLeft: 54 }}>{info.desc}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: grade.enabled ? 1 : 0.4 }}>
        <span style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>Weight</span>
        <input
          type="number" min="0" max="100" value={grade.weight} disabled={!grade.enabled}
          onChange={e => onChange({ weight: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
          className="form-input"
          style={{ width: 80, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
        />
      </div>
    </div>
  </div>
);

/* ── Score row (label + input) ───────────────────────────── */
const ScoreRow = ({ label, value, onChange, suffix, description }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '10px 0' }}>
    <div style={{ flex: 1 }}>
      <label className="form-label" style={{ marginBottom: 0 }}>{label}</label>
      {description && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{description}</div>}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="number" min="0" max={suffix === '%' ? 100 : 999} value={value}
        onChange={e => onChange(Math.max(0, parseInt(e.target.value) || 0))}
        className="form-input"
        style={{ width: 80, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
      />
      <span style={{ fontSize: 12, color: '#888' }}>{suffix || ''}</span>
    </div>
  </div>
);

/* ── Main page ───────────────────────────────────────────── */
export const AdminAbilitiesScorer = () => {
  const [config, setConfig] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const mergeConfig = (d) => ({
    grades: {
      grade1: { ...DEFAULTS.grades.grade1, ...d.grades?.grade1 },
      grade2: { ...DEFAULTS.grades.grade2, ...d.grades?.grade2 },
      grade3: { ...DEFAULTS.grades.grade3, ...d.grades?.grade3 },
      grade4: { ...DEFAULTS.grades.grade4, ...d.grades?.grade4 },
    },
    durationBoost: { ...DEFAULTS.durationBoost, ...d.durationBoost },
  });

  useEffect(() => {
    matchingModelAPI.getAbilitiesConfig()
      .then(({ data }) => { if (data.data) setConfig(mergeConfig(data.data)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await matchingModelAPI.updateAbilitiesConfig(config);
      setConfig(mergeConfig(data.data));
      toast.success('Abilities scorer config saved');
    } catch { /* interceptor toasts */ }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    setConfig(DEFAULTS);
    toast.success('Reset to defaults');
  };

  const updateGrade = (key) => (patch) =>
    setConfig(c => ({ ...c, grades: { ...c.grades, [key]: { ...c.grades[key], ...patch } } }));

  const updateDuration = (patch) =>
    setConfig(c => ({ ...c, durationBoost: { ...c.durationBoost, ...patch } }));

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spinner size={32} /></div>;

  // Calculate total weight of enabled grades for display
  const enabledGrades = GRADE_INFO.filter(g => config.grades[g.key].enabled);
  const totalWeight = enabledGrades.reduce((sum, g) => sum + config.grades[g.key].weight, 0);

  return (
    <div>
      <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
        Configure semantic matching between seeker experience roles &amp; tasks and position roles &amp; tasks.
        Matching uses cosine similarity at different granularity levels.
        Enable or disable each grade and set its relative weight.
      </p>

      {/* ── Granularity Grades ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Layers size={18} />
          <span>Granularity Grades</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          {GRADE_INFO.map((info, i) => (
            <React.Fragment key={info.key}>
              {i > 0 && <div style={{ borderTop: '1px solid #eee' }} />}
              <GradeRow info={info} grade={config.grades[info.key]} onChange={updateGrade(info.key)} />
            </React.Fragment>
          ))}

          {/* Weight summary */}
          <div style={{ borderTop: '1px solid #ddd', marginTop: 8, paddingTop: 12,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#666' }}>
              {enabledGrades.length} of 4 grades enabled
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: totalWeight === 100 ? '#28A745' : '#CC0000' }}>
              Total weight: {totalWeight} / 100
            </span>
          </div>
          {totalWeight !== 100 && (
            <div style={{ fontSize: 12, color: '#CC0000', textAlign: 'right', marginTop: 4 }}>
              Weights of enabled grades should sum to 100. They will be normalised at scoring time.
            </div>
          )}
        </div>
      </div>

      {/* ── Duration Boost ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Clock size={18} />
          <span>Experience Duration Boost</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            Longer experience in matching roles strengthens the similarity score.
            The boost follows a diminishing-returns curve — each additional month adds less than the previous one.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <label style={{ position: 'relative', display: 'inline-block', width: 42, height: 24, flexShrink: 0 }}>
              <input type="checkbox" checked={config.durationBoost.enabled}
                onChange={e => updateDuration({ enabled: e.target.checked })}
                style={{ opacity: 0, width: 0, height: 0 }} />
              <span style={{
                position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: 12,
                background: config.durationBoost.enabled ? '#28A745' : '#ccc', transition: 'background 0.2s',
              }}>
                <span style={{
                  position: 'absolute', height: 18, width: 18, left: config.durationBoost.enabled ? 20 : 4, top: 3,
                  background: '#fff', borderRadius: '50%', transition: 'left 0.2s',
                }} />
              </span>
            </label>
            <label className="form-label" style={{ marginBottom: 0 }}>Enable Duration Boost</label>
          </div>

          <div style={{ opacity: config.durationBoost.enabled ? 1 : 0.4 }}>
            <ScoreRow label="Max Boost" value={config.durationBoost.maxBoostPercent}
              onChange={v => updateDuration({ maxBoostPercent: Math.min(100, v) })} suffix="%"
              description="Maximum percentage increase to the cosine similarity score from experience duration." />
            <div style={{ borderTop: '1px solid #eee' }} />
            <ScoreRow label="Half-Life" value={config.durationBoost.halfLifeMonths}
              onChange={v => updateDuration({ halfLifeMonths: Math.min(999, v) })} suffix="months"
              description="Number of months at which the boost reaches half its maximum value." />
          </div>
        </div>
      </div>

      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={handleReset} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RotateCcw size={15} /><span>Reset Defaults</span>
        </button>
        <button onClick={handleSave} disabled={saving}
          className="btn btn-success" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {saving ? <Spinner size={16} /> : <><Save size={15} /><span>Save Configuration</span></>}
        </button>
      </div>
    </div>
  );
};
